import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, CheckCircle2, LayoutDashboard, UploadCloud, Search, MessageSquare, Zap, Smartphone, Mail, Settings } from 'lucide-react';
import MarketingLayout from '../../components/Marketing/MarketingLayout';

const FeaturesPage = () => {
  const navigate = useNavigate();

  const featureCardStyle = {
    background: '#ffffff',
    padding: '40px 32px',
    borderRadius: '24px',
    border: '1px solid #f1f5f9',
    transition: 'all 0.3s ease',
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.02)'
  };

  const featureList = [
    {
      icon: <LayoutDashboard size={24} color="#3b82f6" />,
      bg: 'rgba(59, 130, 246, 0.1)',
      title: 'Lead Management',
      desc: 'Manage every lead from a single, organized dashboard.',
      points: [
        'Add, edit, and track leads through every stage of your pipeline',
        'Assign leads to team members',
        'Filter and search through your entire lead database instantly',
        'Never lose track of a follow-up again'
      ]
    },
    {
      icon: <UploadCloud size={24} color="#10b981" />,
      bg: 'rgba(16, 185, 129, 0.1)',
      title: 'Bulk Lead Upload',
      desc: 'Built for agencies that already have large lead databases.',
      points: [
        'Upload existing leads in bulk via file import',
        'Automatically organize uploaded leads into your CRM',
        'Save hours of manual data entry',
        'Ideal for agencies migrating from spreadsheets or other tools'
      ]
    },
    {
      icon: <Search size={24} color="#f59e0b" />,
      bg: 'rgba(245, 158, 11, 0.1)',
      title: 'Lead Scraper',
      desc: 'Generate new leads without leaving the platform.',
      points: [
        'Built-in scraping tool to discover and collect fresh leads',
        'Automatically stores scraped leads directly into your CRM database',
        'Combine scraped leads with uploaded leads in one unified pipeline',
        'A great option for businesses building their lead list from scratch'
      ]
    },
    {
      icon: <MessageSquare size={24} color="#8b5cf6" />,
      bg: 'rgba(139, 92, 246, 0.1)',
      title: 'WhatsApp API Integration',
      desc: 'Connect your WhatsApp Business API for scalable, automated communication.',
      points: [
        'Send messages directly through the official WhatsApp API',
        'Automate replies and follow-ups',
        'Built for high-volume, reliable messaging'
      ]
    },
    {
      icon: <Smartphone size={24} color="#0ea5e9" />,
      bg: 'rgba(14, 165, 233, 0.1)',
      title: 'WhatsApp Web Integration',
      desc: 'Prefer a simpler setup? Use WhatsApp Web directly inside CRM Automax.',
      points: [
        'No complex API setup required',
        'Message leads directly through your connected WhatsApp Web session',
        'Great for smaller teams or quick outreach'
      ]
    },
    {
      icon: <Zap size={24} color="#ec4899" />,
      bg: 'rgba(236, 72, 153, 0.1)',
      title: 'Bulk WhatsApp Messaging',
      desc: 'Reach your entire lead list in just a few clicks.',
      points: [
        'Send bulk messages via WhatsApp API or WhatsApp Web (Web JS)',
        'Use for promotions, updates, reminders, and follow-ups',
        'Choose the sending method that fits your workflow'
      ]
    },
    {
      icon: <Mail size={24} color="#f43f5e" />,
      bg: 'rgba(244, 63, 94, 0.1)',
      title: 'Bulk Email Sender',
      desc: 'Send email campaigns at scale, right from your CRM.',
      points: [
        'Send bulk emails to your entire lead database',
        'No need for a separate email marketing tool'
      ]
    },
    {
      icon: <Settings size={24} color="#64748b" />,
      bg: 'rgba(100, 116, 139, 0.1)',
      title: 'Email Campaign Management',
      desc: 'Plan and manage complete email campaigns.',
      points: [
        'Create and schedule email campaigns',
        'Manage your messaging strategy across your lead list',
        'Keep all your outreach connected to the leads you\'re already managing'
      ]
    }
  ];

  return (
    <MarketingLayout 
      title="Features | CRM Automax — Lead Scraper, WhatsApp API & Bulk Email Tools"
      metaDescription="Explore CRM Automax features: lead management, bulk lead upload, lead scraping, WhatsApp API & Web integration, bulk WhatsApp messaging, and bulk email campaigns."
    >
      {/* ── Page Header ── */}
      <section className="marketing-hero-section" style={{
        padding: '100px 48px 80px',
        textAlign: 'center',
        background: 'linear-gradient(to bottom, #eff6ff 0%, #ffffff 100%)'
      }}>
        <h1 className="animate-fade-up" style={{ 
          fontSize: 'clamp(40px, 6vw, 56px)', fontWeight: '900', color: '#1e293b', 
          lineHeight: 1.1, letterSpacing: '-0.04em', maxWidth: '800px', 
          margin: '0 auto 24px'
        }}>
          Everything You Need to <span style={{ color: '#3b82f6' }}>Capture, Manage, and Convert Leads</span>
        </h1>
        <p className="animate-fade-up" style={{ 
          fontSize: '20px', color: '#64748b', maxWidth: '700px', 
          margin: '0 auto', lineHeight: 1.6, animationDelay: '0.1s'
        }}>
          CRM Automax brings lead management and outreach automation together — built for businesses and agencies that move fast and handle high lead volumes.
        </p>
      </section>

      {/* ── Features Grid ── */}
      <section className="marketing-content-section" style={{ padding: '40px 48px 120px', maxWidth: '1300px', margin: '0 auto' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))', gap: '32px' }}>
          {featureList.map((feature, idx) => (
            <div key={idx} className="feature-card animate-fade-up" style={{ ...featureCardStyle, animationDelay: `${0.1 * (idx + 1)}s` }}>
              <div style={{ width: '56px', height: '56px', borderRadius: '16px', background: feature.bg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {feature.icon}
              </div>
              <div>
                <h3 style={{ fontSize: '24px', fontWeight: '800', color: '#1e293b', marginBottom: '12px' }}>{feature.title}</h3>
                <p style={{ fontSize: '16px', color: '#64748b', lineHeight: 1.6, marginBottom: '24px' }}>{feature.desc}</p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {feature.points.map((point, pIdx) => (
                    <div key={pIdx} style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', fontSize: '15px', color: '#475569', lineHeight: 1.5 }}>
                      <CheckCircle2 size={18} color="#10b981" style={{ flexShrink: 0, marginTop: '2px' }} />
                      <span>{point}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ── Bottom CTA ── */}
      <section className="marketing-content-section" style={{ padding: '0 48px 120px', textAlign: 'center' }}>
        <div style={{ 
          maxWidth: '1000px', margin: '0 auto', background: 'linear-gradient(135deg, #3b82f6, #2563eb)', 
          padding: '80px 40px', borderRadius: '40px', color: 'white', position: 'relative', overflow: 'hidden'
        }}>
          <h2 style={{ fontSize: '42px', fontWeight: '800', marginBottom: '20px', position: 'relative', zIndex: 1 }}>All these features. One affordable platform.</h2>
          <div style={{ display: 'flex', justifyContent: 'center', gap: '16px', position: 'relative', zIndex: 1, marginTop: '40px', flexWrap: 'wrap' }}>
            <button onClick={() => navigate('/pricing')} style={{
              background: 'white', color: '#3b82f6', border: 'none',
              padding: '16px 32px', borderRadius: '12px', fontWeight: '700', fontSize: '16px', cursor: 'pointer',
              boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)'
            }}>
              Check Pricing
            </button>
            <button onClick={() => navigate('/register')} style={{
              background: 'transparent', color: 'white', border: '1px solid rgba(255,255,255,0.3)',
              padding: '16px 32px', borderRadius: '12px', fontWeight: '700', fontSize: '16px', cursor: 'pointer',
              transition: 'background 0.2s ease'
            }} onMouseOver={(e) => e.target.style.background = 'rgba(255,255,255,0.1)'} onMouseOut={(e) => e.target.style.background = 'transparent'}>
              Get Started
            </button>
          </div>
        </div>
      </section>
    </MarketingLayout>
  );
};

export default FeaturesPage;
